import type { AxiosError } from 'axios'
import {
  AGENCY_VIEW_ONLY_REASON,
  AGENCY_VIEW_ONLY_REFUSAL_MESSAGE,
  describeMissingStaffPermission,
  GENERIC_FORBIDDEN_MESSAGE,
  isStaffPermissionKey,
} from '@repo/shared'
import { PLAN_FEATURES, type PlanFeature } from '@repo/shared/billing'
import { planUpgradeMessage } from './plan-feature-copy'

interface ApiErrorResponse {
  success: false
  error: {
    code: string
    message: string
  }
}

export function isConflictError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const axiosError = error as AxiosError<ApiErrorResponse>
  return axiosError.response?.status === 409
}

/**
 * A refusal decided in the browser before any request is sent (e.g. the calendar's own permission
 * checks). Its message is written for salon staff, so `getApiErrorMessage` shows it as-is.
 */
export class LocalActionError extends Error {
  override name = 'LocalActionError'
}

type ApiErrorBody = {
  error?: { message?: string; details?: { permission?: unknown; reason?: unknown; feature?: unknown } }
}

/** The API refuses a Growth+ action the salon's plan does not include (403, `details.feature` names it). */
export const PLAN_UPGRADE_REQUIRED_REASON = 'PLAN_UPGRADE_REQUIRED'

function isPlanFeature(value: unknown): value is PlanFeature {
  return typeof value === 'string' && (PLAN_FEATURES as readonly string[]).includes(value)
}

/** The plan feature a `PLAN_UPGRADE_REQUIRED` refusal names, or null for any other error. */
export function getPlanUpgradeFeature(error: unknown): PlanFeature | null {
  const details = (error as { response?: { status?: number; data?: ApiErrorBody } } | null)?.response?.data?.error?.details
  if (details?.reason !== PLAN_UPGRADE_REQUIRED_REASON) return null
  return isPlanFeature(details.feature) ? details.feature : null
}

/** Server refusals whose sentence the web app words itself (`error.details.reason` → copy). */
export const NO_SHOW_FEE_CHARGED_REASON = 'NO_SHOW_FEE_CHARGED'
const REASON_MESSAGES: Record<string, string> = {
  // A charged no-show / late-cancel fee pins the visit (status change, delete, split, profile split).
  [NO_SHOW_FEE_CHARGED_REASON]: 'This visit has a charged fee. Refund it first.',
}

/**
 * A sentence a salon worker understands, for any failed request:
 * - refused (403): names the missing staff permission, explains agency view-only, names the plan that
 *   includes a Growth+ feature, or uses the server's sentence — never "Request failed with status code 403";
 * - a refusal the web app words itself (`REASON_MESSAGES`);
 * - anything else: the API's `error.message`, or `fallback` for network / unexpected errors.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof LocalActionError) return error.message
  const response = (error as { response?: { status?: number; data?: ApiErrorBody } } | null)?.response
  const apiError = response?.data?.error
  if (response?.status === 403) {
    if (apiError?.details?.reason === AGENCY_VIEW_ONLY_REASON) return AGENCY_VIEW_ONLY_REFUSAL_MESSAGE
    const planFeature = getPlanUpgradeFeature(error)
    if (planFeature) return planUpgradeMessage(planFeature)
    const permission = apiError?.details?.permission
    if (isStaffPermissionKey(permission)) return describeMissingStaffPermission(permission)
    return apiError?.message || GENERIC_FORBIDDEN_MESSAGE
  }
  const reason = apiError?.details?.reason
  if (typeof reason === 'string' && REASON_MESSAGES[reason]) return REASON_MESSAGES[reason]
  return apiError?.message ?? fallback
}

/** The API's `error.details` object (e.g. `{ reason, minCapCents }`), or `{}`. */
export function getApiErrorDetails(error: unknown): Record<string, unknown> {
  const data = (error as { response?: { data?: { error?: { details?: Record<string, unknown> } } } } | null)?.response?.data
  return data?.error?.details ?? {}
}

/** The API's machine-readable `error.details.reason` (e.g. `ARRIVAL_NOT_WAITING`), if any. */
export function getApiErrorReason(error: unknown): string | null {
  const data = (error as { response?: { data?: { error?: { details?: { reason?: string } } } } })?.response?.data
  return data?.error?.details?.reason ?? null
}

/** No answer at all (network, timeout): the request may still have gone through on the server. */
export function isNoResponseError(error: unknown): boolean {
  return !(error as { response?: unknown } | null)?.response
}

export function isNotFoundError(error: unknown): boolean {
  return (error as AxiosError | null)?.response?.status === 404
}

export function isForbiddenError(error: unknown): boolean {
  return (error as AxiosError | null)?.response?.status === 403
}
